import { SettingsHeading } from '../components/settings/SettingsAppearance';
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import axios from 'axios'
import MeunSideSetting from '../components/settings/MeunSideSetting'
import { MwButton, MwInputText, NoPermission } from '../components/ui'
import MwInputFile from '../components/ui/MwInputFile.jsx'
import AlertMsg from '../components/ui/AlertMsg'
import { useFetch, useCurrent, usePost, usePut } from '../hooks'
import GeneralSettings from './settings/GeneralSettings'
import InvoiceSettings from './settings/InvoiceSettings'
import MyCompany from './settings/MyCompany'
import MyBranches from './settings/MyBranches'
import QuotationSettings from './settings/QuotationSettings'
import PaymentMethodSettings from './settings/PaymentMethodSettings'
import ProductServiceSettings from './settings/ProductServiceSettings'
import PurchasesInvoiceSettings from './settings/PurchasesInvoiceSettings'
import PurchasesQuoteSettings from './settings/PurchasesQuoteSettings'
import PurchasesPaymentMethodSettings from './settings/PurchasesPaymentMethodSettings'
import TaxPage from '../components/OsVatComponent/VatPage'
import CurrencyPage from '../components/Currency/CurrencyPage'
import LanguagesSetting from './settings/LanguagesSetting'
import TreasurySettings from './settings/TreasurySettings'
import BankAccountsSettings from './settings/BankAccountsSettings'
import ReceiptVoucherTypes from './settings/ReceiptVoucherTypes'
import PaymentVoucherTypes from './settings/PaymentVoucherTypes'
import { useTranslation } from 'react-i18next'
import { useAppsHome } from '../data/AbsatApps.jsx'
import { useDispatch } from 'react-redux'
import { setisLogging } from '../store/authSlice.js'
import { setSettings } from '../store/settingSlice.js'
import { arrayToObjKeyValue } from '../utils/global.js'
import { buildCentralSettingsCacheKey } from '../utils/centralSettingsCache.js'
import { findAppByModule, userHasAppAccess } from '../utils/appAccess.js'
import { ContractsSettingsPanel, contractsSettingsSections } from '../modules/contracts'

const CENTRAL_SETTINGS_SCOPE = 'global';

const Settings = () => {

    const [activeMenu,setActiveMenu] = useState('GeneralSettings')
    const [activeTab, setActiveTab] = useState('general')
    const [activeFinanceMenu, setActiveFinanceMenu] = useState('Treasury')
    const [activePurchasesMenu, setActivePurchasesMenu] = useState('PurchasesInvoiceSettings')
    const [activeContractsMenu, setActiveContractsMenu] = useState(contractsSettingsSections[0].key)
    const params = useParams();
    const [t] = useTranslation('global');
    const usernameTenant = params.tenant
    const [loading,setLoading] = useState(false);
    const { apps } = useAppsHome();
    const { user, currentCompanyId, currentTenantId } = useCurrent();
    const { loading: savingPassword, putData } = usePut();
    const { data: uploadResult, error: uploadError, postData: uploadFile } = usePost(false);
    const dispatch = useDispatch();
    const [profileName, setProfileName] = useState(user?.name || '');
    const [profileMobile, setProfileMobile] = useState(user?.mobile || '');
    const [profileEmail, setProfileEmail] = useState(user?.email || '');
    const [profileJobTitle, setProfileJobTitle] = useState(user?.jobTitle || '');
    const [profilePic, setProfilePic] = useState(user?.userPic || '');
    const [profileAlert, setProfileAlert] = useState(null);
    const [uploadingPic, setUploadingPic] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [passwordAlert, setPasswordAlert] = useState(null);
    const {data:defineSettingData,loading:defineSettingLoading} = useFetch(`${process.env.REACT_APP_Auth_API_BASE_URL}/defineSettings`); 

    const filterDefineSetting = (settingName) => {
        return defineSettingData?.data.filter(item => item.settingName === settingName)[0]
    }
    const canViewSalesSettings = userHasAppAccess(user, apps, 'sales');
    const canViewPurchasesSettings = userHasAppAccess(user, apps, 'purchases');
    const canViewFinanceSettings = userHasAppAccess(user, apps, 'finance');
    // Contracts module: same rule as its route (App.js), open to all until the app is registered in the auth service.
    const canViewContractsSettings = !findAppByModule(apps, 'contracts') || userHasAppAccess(user, apps, 'contracts');

    const generalMenuIds = useMemo(() => ([
        'MyAccount',
        'GeneralSettings',
        'MyCompany',
        'MyBranches',
        'ChangePassword',
        'VatSettings',
        'CurrencySettings',
        'LanguagesSetting',
        'ProductServiceSettings',
    ]), []);

    const salesMenuIds = useMemo(() => ([
        'InvoiceSettings',
        'QuoteSettings',
        'PaymentMethod',
    ]), []);

    const financeMenuIds = useMemo(() => ([
        'Treasury',
        'account',
        'incomes',
        'expenses',
    ]), []);

    const purchasesMenuIds = useMemo(() => ([
        'PurchasesInvoiceSettings',
        'PurchasesQuoteSettings',
        'PurchasesPaymentMethod',
    ]), []);

    const setTabAndDefaultMenu = (tabId) => {
        setActiveTab(tabId);
        if (tabId === 'general') {
            if (!generalMenuIds.includes(activeMenu)) setActiveMenu('GeneralSettings');
        }
        if (tabId === 'sales') {
            if (!salesMenuIds.includes(activeMenu)) setActiveMenu('InvoiceSettings');
        }
        if (tabId === 'finance') {
            if (!financeMenuIds.includes(activeFinanceMenu)) setActiveFinanceMenu('Treasury');
        }
        if (tabId === 'purchases') {
            if (!purchasesMenuIds.includes(activePurchasesMenu)) setActivePurchasesMenu('PurchasesInvoiceSettings');
        }
    }

    const refreshTenant = async() => {
        setLoading(true);
        try{
            const res = await axios.get(`${process.env.REACT_APP_Auth_API_BASE_URL}/tenants/username/${usernameTenant}`);
            const _data = res?.data;
            if(_data){
                localStorage.setItem('tenant',JSON.stringify( _data?.data));
                console.log('refreshTenant', _data?.data);
            }
            setLoading(false);
        }catch(err){
            console.log(err);
            setLoading(false);
        }
    }

    const location = useLocation();
    useEffect(() => {
        setProfileName(user?.name || '');
        setProfileMobile(user?.mobile || '');
        setProfileEmail(user?.email || '');
        setProfileJobTitle(user?.jobTitle || '');
        setProfilePic(user?.userPic || '');
    }, [user?.email, user?.jobTitle, user?.mobile, user?.name, user?.userPic]);

    useEffect(() => {
        if (uploadResult?.data?.location) {
            setProfilePic(uploadResult.data.location);
            setUploadingPic(false);
        }
    }, [uploadResult]);

    useEffect(() => {
        if (uploadError) {
            setUploadingPic(false);
            setProfileAlert({ type: 'Error', msg: t('setting.profile.errors.uploadFailed') });
        }
    }, [t, uploadError]);

    useEffect(() => {
        if (location.state?.tab === 'profile') {
            setActiveTab('general');
            setActiveMenu('MyAccount');
            return;
        }
        if (location.state?.tab === 'password') {
            setActiveTab('general');
            setActiveMenu('ChangePassword');
            return;
        }
        if (location.state?.activeMenuTarget) {
            const rawTarget = location.state.activeMenuTarget;
            const mappedTarget =
                rawTarget === 'QuotationSettings' ? 'QuoteSettings'
                : rawTarget === 'PaymentMethodSettings' ? 'PaymentMethod'
                : rawTarget === 'TaxSettings' ? 'VatSettings'
                : rawTarget;
            if (financeMenuIds.includes(mappedTarget)) {
                setActiveFinanceMenu(mappedTarget);
            } else if (purchasesMenuIds.includes(mappedTarget)) {
                setActivePurchasesMenu(mappedTarget);
            } else {
                setActiveMenu(mappedTarget);
            }
            if (salesMenuIds.includes(mappedTarget) && canViewSalesSettings) setActiveTab('sales');
            if (generalMenuIds.includes(mappedTarget)) setActiveTab('general');
            if (financeMenuIds.includes(mappedTarget) && canViewFinanceSettings) setActiveTab('finance');
            if (purchasesMenuIds.includes(mappedTarget) && canViewPurchasesSettings) setActiveTab('purchases');
        }
    }, [canViewFinanceSettings, canViewPurchasesSettings, canViewSalesSettings, financeMenuIds, generalMenuIds, location.state, purchasesMenuIds, salesMenuIds]);

    useEffect(() => {
        if (activeTab === 'general' && salesMenuIds.includes(activeMenu)) setActiveMenu('GeneralSettings');
        if (activeTab === 'sales' && generalMenuIds.includes(activeMenu)) setActiveMenu('InvoiceSettings');
        if (activeTab === 'purchases' && purchasesMenuIds.includes(activePurchasesMenu) === false) {
            setActivePurchasesMenu('PurchasesInvoiceSettings');
        }
    }, [activeMenu, activePurchasesMenu, activeTab, generalMenuIds, purchasesMenuIds, salesMenuIds]);

    useEffect(() => {
        if (activeTab === 'sales' && !canViewSalesSettings) setActiveTab('general');
        if (activeTab === 'purchases' && !canViewPurchasesSettings) setActiveTab('general');
        if (activeTab === 'finance' && !canViewFinanceSettings) setActiveTab('general');
        if (activeTab === 'contracts' && !canViewContractsSettings) setActiveTab('general');
    }, [activeTab, canViewContractsSettings, canViewFinanceSettings, canViewPurchasesSettings, canViewSalesSettings]);

    useEffect(() => {
        const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        const waitForToken = async () => {
            for (let attempt = 0; attempt < 6; attempt += 1) {
                const token = localStorage.getItem('token');
                if (token) return token;
                await sleep(250 * (attempt + 1));
            }
            return null;
        };
        const normalizeSettingsBlock = (value) => {
            if (!value) return {};
            if (Array.isArray(value)) return arrayToObjKeyValue(value);
            return value;
        };
        const refreshCentralSettings = async () => {
            if (!currentTenantId || !currentCompanyId) return;
            const token = await waitForToken();
            if (!token) return;
            const cacheKey = buildCentralSettingsCacheKey(currentTenantId, currentCompanyId, CENTRAL_SETTINGS_SCOPE);
            const cachedRaw = (() => {
                try {
                    return JSON.parse(localStorage.getItem(cacheKey));
                } catch {
                    return null;
                }
            })();
            const headers = { Authorization: `Bearer ${token}` };
            if (cachedRaw?.version) headers['If-None-Match'] = String(cachedRaw.version);
            const url = `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${currentTenantId}&companyId=${currentCompanyId}&scope=${CENTRAL_SETTINGS_SCOPE}`;
            const response = await axios.get(url, { headers, validateStatus: () => true });
            if (response.status === 304) return;
            if (response?.data?.status !== 1) return;
            const nextData = response?.data?.data || {};
            const nextVersion = response?.data?.version ?? cachedRaw?.version ?? Date.now();
            localStorage.setItem(cacheKey, JSON.stringify({ data: nextData, version: nextVersion }));
            dispatch(setSettings({
                invoiceSettings: normalizeSettingsBlock(nextData?.invoiceSettings),
                generalSettings: normalizeSettingsBlock(nextData?.generalSettings),
                quoteSettings: normalizeSettingsBlock(nextData?.quoteSettings)
            }));
        };
        refreshCentralSettings();
    }, [currentCompanyId, currentTenantId, dispatch]);

    const normalizeUrl = (value) => String(value || "").trim().replace(/\/+$/, "");
    const financeApp = apps?.find(a => String(a?.name || "").toLowerCase() === 'financial')
        || apps?.find(a => String(a?.name || "").toLowerCase().includes('finance'));
    const purchasesApp = apps?.find(a => String(a?.name || "").toLowerCase() === 'purchases')
        || apps?.find(a => String(a?.name || "").toLowerCase().includes('purchase'))
        || apps?.find(a => String(a?.titleAr || "").includes('مشتريات'))
        || apps?.find(a => String(a?.titleEn || "").toLowerCase().includes('purchase'));
    const invApiBase = normalizeUrl(process.env.REACT_APP_INV_API_SERVER_BASE_URL);
    const fallbackPurchasesBase = invApiBase ? invApiBase.replace(/\/sales(\/|$)/i, "/purchases$1") : "";
    const financeApiBaseUrlResolved = normalizeUrl("https://api.absat-erp.com/finance");
    const purchasesApiBaseUrlResolvedRaw = normalizeUrl(
        purchasesApp?.apiBaseUrl || purchasesApp?.apiUrl || purchasesApp?.api_base_url || fallbackPurchasesBase
    );
    const purchasesApiBaseUrlResolved = purchasesApiBaseUrlResolvedRaw
        ? purchasesApiBaseUrlResolvedRaw.replace(/\/sales(\/|$)/i, "/purchases$1")
        : "";

    const uploadProfilePic = (e) => {
        const file = e?.target?.files?.[0] || null;
        if (!file) return;
        setProfileAlert(null);
        setUploadingPic(true);
        const formData = new FormData();
        formData.append('myfile', file, file.name);
        uploadFile(`${process.env.REACT_APP_UPLOADS_API_BASE_URL}/users`, formData);
    };

    const submitProfileUpdate = async (e) => {
        e.preventDefault();
        setProfileAlert(null);

        const name = String(profileName || '').trim();
        const mobile = String(profileMobile || '').trim();
        const email = String(profileEmail || '').trim();

        if (!name) {
            setProfileAlert({ type: 'Error', msg: t('setting.profile.errors.nameRequired') });
            return;
        }
        if (!email) {
            setProfileAlert({ type: 'Error', msg: t('setting.profile.errors.emailRequired') });
            return;
        }

        try {
            const result = await putData(
                `${process.env.REACT_APP_Auth_API_BASE_URL}/users/update`,
                { name, mobile, email, jobTitle: profileJobTitle, userPic: profilePic }
            );
            const updatedUser = result?.data?.user || result?.data || result?.user || null;
            if (result?.status === 1 || result?.status === true || updatedUser) {
                const payload = updatedUser || { ...user, name, mobile, email, jobTitle: profileJobTitle, userPic: profilePic };
                localStorage.setItem('user', JSON.stringify(payload));
                localStorage.setItem('userCurrentTs', Date.now().toString());
                dispatch(setisLogging({ user: payload, isLogging: true }));
                setProfileAlert({ type: 'Success', msg: t('setting.profile.success') });
                return;
            }
            setProfileAlert({ type: 'Error', msg: result?.message || t('setting.profile.errors.failed') });
        } catch (err) {
            const apiMsg = err?.response?.data?.message;
            setProfileAlert({ type: 'Error', msg: apiMsg || t('setting.profile.errors.failed') });
        }
    };

    const submitChangePassword = async (e) => {
        e.preventDefault();
        setPasswordAlert(null);

        const current = String(currentPassword || '').trim();
        const next = String(newPassword || '').trim();
        const confirm = String(confirmNewPassword || '').trim();

        if (!current || !next || !confirm) {
            setPasswordAlert({ type: 'Error', msg: t('setting.password.errors.required') });
            return;
        }
        if (next !== confirm) {
            setPasswordAlert({ type: 'Error', msg: t('setting.password.errors.mismatch') });
            return;
        }
        if (next.length < 6) {
            setPasswordAlert({ type: 'Error', msg: t('setting.password.errors.tooShort') });
            return;
        }

        try {
            const result = await putData(
                `${process.env.REACT_APP_Auth_API_BASE_URL}/users/change-password`,
                { currentPassword: current, newPassword: next }
            );
            if (result?.status === 1) {
                setCurrentPassword('');
                setNewPassword('');
                setConfirmNewPassword('');
                setPasswordAlert({ type: 'Success', msg: t('setting.password.success') });
                return;
            }
            setPasswordAlert({ type: 'Error', msg: result?.message || t('setting.password.errors.failed') });
        } catch (err) {
            const apiMsg = err?.response?.data?.message;
            setPasswordAlert({ type: 'Error', msg: apiMsg || t('setting.password.errors.failed') });
        }
    };

    return (
        <>
            <SettingsHeading title={t("setting.System_Settings")} />
            <div className='w-full'>
                <div className='settings-product-tabs'>
                    <button
                        type="button"
                        onClick={() => setTabAndDefaultMenu('general')}
                        className={`px-4 py-2 rounded-md text-sm font-semibold border border-[#D1CBE6] transition-[0.3s] ${activeTab === 'general' ? 'bg-[#261759] text-white' : 'bg-white text-[#261759] hover:bg-[#E2DFF1]'}`}
                    >
                        {t("setting.tabGeneral")}
                    </button>
                    {canViewSalesSettings && (
                    <button
                        type="button"
                        onClick={() => setTabAndDefaultMenu('sales')}
                        className={`px-4 py-2 rounded-md text-sm font-semibold border border-[#D1CBE6] transition-[0.3s] ${activeTab === 'sales' ? 'bg-[#261759] text-white' : 'bg-white text-[#261759] hover:bg-[#E2DFF1]'}`}
                    >
                        {t("setting.tabSales")}
                    </button>
                    )}
                    {canViewPurchasesSettings && (
                    <button
                        type="button"
                        onClick={() => setTabAndDefaultMenu('purchases')}
                        className={`px-4 py-2 rounded-md text-sm font-semibold border border-[#D1CBE6] transition-[0.3s] ${activeTab === 'purchases' ? 'bg-[#261759] text-white' : 'bg-white text-[#261759] hover:bg-[#E2DFF1]'}`}
                    >
                        {t("setting.tabPurchases")}
                    </button>
                    )}
                    {canViewFinanceSettings && (
                    <button
                        type="button"
                        onClick={() => setTabAndDefaultMenu('finance')}
                        className={`px-4 py-2 rounded-md text-sm font-semibold border border-[#D1CBE6] transition-[0.3s] ${activeTab === 'finance' ? 'bg-[#261759] text-white' : 'bg-white text-[#261759] hover:bg-[#E2DFF1]'}`}
                    >
                        {t("setting.tabFinance")}
                    </button>
                    )}
                    {canViewContractsSettings && (
                    <button
                        type="button"
                        onClick={() => setTabAndDefaultMenu('contracts')}
                        className={`px-4 py-2 rounded-md text-sm font-semibold border border-[#D1CBE6] transition-[0.3s] ${activeTab === 'contracts' ? 'bg-[#261759] text-white' : 'bg-white text-[#261759] hover:bg-[#E2DFF1]'}`}
                    >
                        {t("setting.tabContracts")}
                    </button>
                    )}
                  
                </div>
            </div>

            <div className='settings-workspace'>
                {activeTab === 'general' && (
                    <MeunSideSetting activeMenu={activeMenu} setActiveMenu={setActiveMenu} group="general" />
                )}
                {activeTab === 'sales' && canViewSalesSettings && (
                    <MeunSideSetting activeMenu={activeMenu} setActiveMenu={setActiveMenu} group="sales" />
                )}
                {activeTab === 'finance' && canViewFinanceSettings && (
                    <MeunSideSetting
                        activeMenu={activeFinanceMenu}
                        setActiveMenu={setActiveFinanceMenu}
                        group="finance"
                    />
                )}
                {activeTab === 'contracts' && canViewContractsSettings && (
                    <MeunSideSetting
                        activeMenu={activeContractsMenu}
                        setActiveMenu={setActiveContractsMenu}
                        group="contracts"
                    />
                )}
                {activeTab === 'purchases' && canViewPurchasesSettings && (
                    <MeunSideSetting
                        activeMenu={activePurchasesMenu}
                        setActiveMenu={setActivePurchasesMenu}
                        group="purchases"
                    />
                )}
                {
                    !loading &&
                    <div className='settings-content'>
                        {activeTab === 'general' && (
                            <>
                                {activeMenu === 'MyAccount' && (
                                    <div className='settings-account-form'>
                                        <div className='text-sm font-semibold text-slate-700 pb-3'>
                                            {t('setting.profile.title')}
                                        </div>

                                        {profileAlert?.msg && (
                                            <AlertMsg type={profileAlert?.type === 'Error' ? 'Error' : undefined}>
                                                {profileAlert.msg}
                                            </AlertMsg>
                                        )}

                                        <form onSubmit={submitProfileUpdate} className='flex flex-col gap-3'>
                                            <div className="flex items-center gap-4">
                                                <div className="w-16 h-16 rounded-full overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0">
                                                    {profilePic ? (
                                                        <img
                                                            src={profilePic}
                                                            alt={profileName || 'profile'}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                        />
                                                    ) : (
                                                        <span className="text-slate-500 font-bold">{(profileName || '?').charAt(0)}</span>
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <MwInputFile
                                                        id="profilePic"
                                                        label={t('setting.profile.photo')}
                                                        acceptTypes="image/*"
                                                        loading={uploadingPic}
                                                        onChangeHandler={uploadProfilePic}
                                                    />
                                                </div>
                                            </div>
                                            <MwInputText
                                                id="profileName"
                                                label={t('setting.profile.name')}
                                                value={profileName}
                                                onChange={(e) => setProfileName(e.target.value)}
                                                classNameInput="text-start"
                                            />
                                            <MwInputText
                                                id="profileJobTitle"
                                                label={t('setting.profile.jobTitle')}
                                                value={profileJobTitle}
                                                onChange={(e) => setProfileJobTitle(e.target.value)}
                                                classNameInput="text-start"
                                            />
                                            <MwInputText
                                                id="profileMobile"
                                                label={t('setting.profile.mobile')}
                                                value={profileMobile}
                                                onChange={(e) => setProfileMobile(e.target.value)}
                                                classNameInput="text-start"
                                            />
                                            <MwInputText
                                                id="profileEmail"
                                                label={t('setting.profile.email')}
                                                value={profileEmail}
                                                onChange={(e) => setProfileEmail(e.target.value)}
                                                classNameInput="text-start"
                                            />

                                            <div className='pt-2 flex items-center gap-2'>
                                                <MwButton
                                                    actionType="submit"
                                                    type="saveBtn"
                                                    disabled={savingPassword || uploadingPic}
                                                >
                                                    {t('setting.profile.save')}
                                                </MwButton>
                                            </div>
                                        </form>
                                    </div>
                                )}
                                {activeMenu === 'GeneralSettings' && defineSettingData && !defineSettingLoading && (
                                    <GeneralSettings filterDefineSetting={filterDefineSetting} refreshTenant={refreshTenant} />
                                )}
                                {activeMenu === 'MyCompany' && <MyCompany refreshTenant={refreshTenant} />}
                                {activeMenu === 'MyBranches' && <MyBranches refreshTenant={refreshTenant} />}
                                {activeMenu === 'ProductServiceSettings' && defineSettingData && !defineSettingLoading && (
                                    <ProductServiceSettings filterDefineSetting={filterDefineSetting} refreshTenant={refreshTenant} />
                                )}
                                {activeMenu === 'VatSettings' && (
                                    <TaxPage />
                                )}
                                {activeMenu === 'CurrencySettings' && (
                                    <CurrencyPage />
                                )}
                                {activeMenu === 'LanguagesSetting' && <LanguagesSetting refreshTenant={refreshTenant} />}
                                {activeMenu === 'ChangePassword' && (
                                    <div className='settings-account-form'>
                                        <div className='text-sm font-semibold text-slate-700 pb-3'>
                                            {t('setting.password.title')}
                                        </div>

                                        {passwordAlert?.msg && (
                                            <AlertMsg type={passwordAlert?.type === 'Error' ? 'Error' : undefined}>
                                                {passwordAlert.msg}
                                            </AlertMsg>
                                        )}

                                        <form onSubmit={submitChangePassword} className='flex flex-col gap-3'>
                                            <MwInputText
                                                id="currentPassword"
                                                label={t('setting.password.currentPassword')}
                                                value={currentPassword}
                                                onChange={(e) => setCurrentPassword(e.target.value)}
                                                inputType="password"
                                                classNameInput="text-start"
                                            />
                                            <MwInputText
                                                id="newPassword"
                                                label={t('setting.password.newPassword')}
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                inputType="password"
                                                classNameInput="text-start"
                                            />
                                            <MwInputText
                                                id="confirmNewPassword"
                                                label={t('setting.password.confirmNewPassword')}
                                                value={confirmNewPassword}
                                                onChange={(e) => setConfirmNewPassword(e.target.value)}
                                                inputType="password"
                                                classNameInput="text-start"
                                            />

                                            <div className='pt-2 flex items-center gap-2'>
                                                <MwButton
                                                    actionType="submit"
                                                    type="saveBtn"
                                                    disabled={savingPassword}
                                                >
                                                    {t('setting.password.save')}
                                                </MwButton>
                                                <div className='text-xs text-slate-400'>
                                                    {user?.username ? `${t('setting.password.userLabel')}: ${user.username}` : ''}
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                )}
                                {generalMenuIds.includes(activeMenu) === false && (
                                    <NoPermission msg={t("settings.noPermission")} />
                                )}
                            </>
                        )}

                        {activeTab === 'sales' && canViewSalesSettings && (
                            <>
                                {activeMenu === 'InvoiceSettings' && defineSettingData && !defineSettingLoading && (
                                    <InvoiceSettings filterDefineSetting={filterDefineSetting} refreshTenant={refreshTenant} />
                                )}
                                {activeMenu === 'QuoteSettings' && defineSettingData && !defineSettingLoading && (
                                    <QuotationSettings filterDefineSetting={filterDefineSetting} refreshTenant={refreshTenant} />
                                )}
                                {activeMenu === 'PaymentMethod' && (
                                    <PaymentMethodSettings
                                        filterDefineSetting={filterDefineSetting}
                                        refreshTenant={refreshTenant}
                                        financeApiBaseUrl={financeApiBaseUrlResolved}
                                    />
                                )}
                                {salesMenuIds.includes(activeMenu) === false && (
                                    <NoPermission msg={t("settings.noPermission")} />
                                )}
                            </>
                        )}

                        {activeTab === 'finance' && canViewFinanceSettings && (
                            <>
                                {activeFinanceMenu === 'Treasury' && <TreasurySettings apiBaseUrl={financeApiBaseUrlResolved} />}
                                {activeFinanceMenu === 'account' && <BankAccountsSettings apiBaseUrl={financeApiBaseUrlResolved} />}
                                {activeFinanceMenu === 'incomes' && <ReceiptVoucherTypes apiBaseUrl={financeApiBaseUrlResolved} />}
                                {activeFinanceMenu === 'expenses' && <PaymentVoucherTypes apiBaseUrl={financeApiBaseUrlResolved} />}
                                {financeMenuIds.includes(activeFinanceMenu) === false && (
                                    <NoPermission msg={t("settings.noPermission")} />
                                )}
                            </>
                        )}

                        {activeTab === 'contracts' && canViewContractsSettings && (
                            <ContractsSettingsPanel section={activeContractsMenu} />
                        )}

                        {activeTab === 'purchases' && canViewPurchasesSettings && (
                            <>
                                {activePurchasesMenu === 'PurchasesInvoiceSettings' && defineSettingData && !defineSettingLoading && (
                                    <PurchasesInvoiceSettings filterDefineSetting={filterDefineSetting} refreshTenant={refreshTenant} />
                                )}
                                {activePurchasesMenu === 'PurchasesQuoteSettings' && defineSettingData && !defineSettingLoading && (
                                    <PurchasesQuoteSettings filterDefineSetting={filterDefineSetting} refreshTenant={refreshTenant} />
                                )}
                                {activePurchasesMenu === 'PurchasesPaymentMethod' && (
                                    <PurchasesPaymentMethodSettings
                                        apiBaseUrl={purchasesApiBaseUrlResolved}
                                        financeApiBaseUrl={financeApiBaseUrlResolved}
                                    />
                                )}
                                {purchasesMenuIds.includes(activePurchasesMenu) === false && (
                                    <NoPermission msg={t("settings.noPermission")} />
                                )}
                            </>
                        )}
                    </div> 
                }
            </div>
        </>
    )

}
export default Settings
